import type { AiContextExplanation } from "../types";

const STORAGE_KEY = "vibetranslations-ai-explanations-v1";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 120;

type CacheEntry = {
  key: string;
  value: AiContextExplanation;
  updatedAt: number;
};

function hash(value: string): string {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36);
}

export function aiExplanationCacheKey(word: string, context: string): string {
  const normalizedWord = word.normalize("NFKC").toLocaleLowerCase().trim();
  const normalizedContext = context.normalize("NFKC").replace(/\s+/g, " ").trim();
  return hash(`${normalizedWord}\n${normalizedContext}`);
}

function readEntries(): CacheEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as CacheEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.updatedAt <= MAX_AGE_MS;
}

export function cleanupAiExplanationCache(): void {
  try {
    const fresh = readEntries().filter(isFresh).slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  } catch {
    // AI enrichment must remain optional when browser storage is unavailable.
  }
}

export function readCachedAiExplanation(
  word: string,
  context: string,
): AiContextExplanation | null {
  const key = aiExplanationCacheKey(word, context);
  return readEntries().find((entry) => entry.key === key && isFresh(entry))?.value ?? null;
}

export function writeCachedAiExplanation(
  word: string,
  context: string,
  value: AiContextExplanation,
): void {
  try {
    const key = aiExplanationCacheKey(word, context);
    const entries = readEntries().filter((entry) => entry.key !== key && isFresh(entry));
    entries.unshift({ key, value, updatedAt: Date.now() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // A storage failure should not hide an explanation that already loaded.
  }
}
